import React, { useState } from 'react'
import styled from 'styled-components'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import { scoreTint } from './AuditQuestionField'

const pct = value => (typeof value === 'number' ? `${Math.round(value * 100)}%` : '—')
const num = value => (typeof value === 'number' ? Number(value.toFixed(1)).toString() : '0')

const ScoreSummaryCard = ({ summary }) => {
  const [showAreas, setShowAreas] = useState(false)
  if (!summary || !summary.possible) return null

  const sections = summary.sections || []
  const met = summary.met
  const tint = met === null || met === undefined ? scoreTint(null) : (met ? scoreTint(1, 1) : scoreTint(0, 1))

  return (
    <Card aria-labelledby='score-summary-title'>
      <Top>
        <div>
          <Eyebrow id='score-summary-title'>Audit score</Eyebrow>
          <Big>{pct(summary.percentage)}</Big>
        </div>
        <Stat>
          <Eyebrow as='div'>Points</Eyebrow>
          <StatValue>{num(summary.score)} / {num(summary.possible)}</StatValue>
        </Stat>
        {typeof summary.target === 'number' && (
          <Stat>
            <Eyebrow as='div'>Target</Eyebrow>
            <Pill $fg={tint.fg} $bg={tint.bg}>
              {pct(summary.target)} · {met ? 'Met' : 'Not met'}
            </Pill>
          </Stat>
        )}
      </Top>
      <Note>
        {summary.na > 0
          ? `${summary.na} ${summary.na === 1 ? 'question' : 'questions'} marked N/A, left out of the possible points.`
          : 'No questions marked N/A.'}
      </Note>

      {sections.length > 1 && (
        <>
          <Toggle type='button' onClick={() => setShowAreas(s => !s)} aria-expanded={showAreas} aria-controls='score-summary-areas'>
            <ExpandMoreRoundedIcon style={{ fontSize: 20, transform: showAreas ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
            {showAreas ? 'Hide score by area' : `Show score by area (${sections.length})`}
          </Toggle>
          {showAreas && (
            <Areas id='score-summary-areas'>
              {sections.map(section => {
                const areaTint = scoreTint(section.score, section.possible)
                return (
                  <Area key={section.key || section.title}>
                    <AreaTitle>{section.title}</AreaTitle>
                    <Bar aria-hidden='true'>
                      <BarFill style={{ width: `${Math.round((section.percentage || 0) * 100)}%`, background: areaTint.fg }} />
                    </Bar>
                    <AreaScore>
                      {section.possible ? `${num(section.score)}/${num(section.possible)} · ${pct(section.percentage)}` : 'All N/A'}
                    </AreaScore>
                  </Area>
                )
              })}
            </Areas>
          )}
        </>
      )}
    </Card>
  )
}

export default ScoreSummaryCard

const Card = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
  padding: 16px;
  border: 1px solid #e4e7ec;
  border-radius: 12px;
  background: #fff;
`

const Top = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 28px;
  flex-wrap: wrap;
`

const Eyebrow = styled.h3`
  margin: 0 0 2px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #667085;
`

const Big = styled.div`
  font-size: 34px;
  font-weight: 800;
  line-height: 1;
  color: #101828;
`

const Stat = styled.div``

const StatValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #101828;
`

const Pill = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
  color: ${({ $fg }) => $fg};
  background: ${({ $bg }) => $bg};
`

const Note = styled.p`
  margin: 0;
  font-size: 13px;
  color: #667085;
`

const Toggle = styled.button`
  font: inherit;
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 40px;
  padding: 6px 10px 6px 6px;
  border: none;
  border-radius: 8px;
  background: none;
  color: #1f7f89;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  &:hover { background: #e6f7f9; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
`

const Areas = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Area = styled.li`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(80px, 1fr) 112px;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr) auto;
  }
`

const AreaTitle = styled.span`
  color: #344054;
`

const Bar = styled.div`
  height: 8px;
  border-radius: 999px;
  background: #f2f4f7;
  overflow: hidden;
  @media (max-width: 600px) { display: none; }
`

const BarFill = styled.div`
  height: 100%;
  border-radius: 999px;
`

const AreaScore = styled.span`
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  color: #101828;
  white-space: nowrap;
  text-align: right;
`
